import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Laptop, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { registerDevice, DEVICE_LIMIT_EVENT } from '@/lib/deviceInfo';

interface BlockedDevice {
  id: string;
  label: string | null;
  last_seen?: string;
}

/**
 * Per-account device limit gate (anti credential-sharing). Listens for the
 * device-limit result broadcast by registerDevice. When enforcement is on and
 * the account is over its device limit, register_device returns allowed:false
 * with the current device list — we show a blocking modal letting the user free
 * a slot (remove a device) or sign out. While enforce=false (the shipped
 * default), allowed is always true, so this renders null and is inert.
 */
export default function DeviceLimitGate() {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [devices, setDevices] = useState<BlockedDevice[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const r = (e as CustomEvent).detail as { allowed?: boolean; devices?: BlockedDevice[] } | undefined;
      if (r && r.allowed === false) setDevices(r.devices ?? []);
      else setDevices(null);
    };
    window.addEventListener(DEVICE_LIMIT_EVENT, handler);
    return () => window.removeEventListener(DEVICE_LIMIT_EVENT, handler);
  }, []);

  const removeDevice = useCallback(async (id: string) => {
    if (!user) return;
    setBusy(id);
    try {
      // RLS allows deleting only the caller's own device rows.
      await supabase.from('user_devices').delete().eq('id', id);
      // Re-attempt registration now that a slot is free; the broadcast result
      // clears this gate if we're allowed.
      await registerDevice(user.id);
    } finally {
      setBusy(null);
    }
  }, [user]);

  if (!devices || !user) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/90 backdrop-blur-sm p-4">
      <div className="w-full max-w-md space-y-4 rounded-none border border-gray-200 dark:border-brand-border-soft bg-white dark:bg-graphite-900 p-6 text-foreground dark:text-white">
        <div className="space-y-1.5">
          <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 block">
            § DEVICES
          </span>
          <h2 className="text-base font-semibold tracking-tight">{t('device_limit_title')}</h2>
          <p className="text-sm text-gray-500 dark:text-neutral-400">{t('device_limit_body')}</p>
        </div>
        <ul className="space-y-2">
          {devices.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 rounded-none border border-gray-200 dark:border-brand-border-soft p-3">
              <div className="flex min-w-0 items-center gap-2">
                <Laptop className="h-4 w-4 shrink-0 text-gray-400 dark:text-neutral-500" />
                <span className="truncate text-sm">{d.label || t('device_limit_unknown_device')}</span>
              </div>
              <button
                disabled={busy === d.id}
                onClick={() => removeDevice(d.id)}
                className="px-4 py-2.5 border border-gray-200 dark:border-brand-border-strong text-foreground dark:text-white hover:bg-gray-100 dark:hover:bg-neutral-900 disabled:opacity-50 text-xs font-mono uppercase tracking-wide transition-colors"
              >
                {busy === d.id ? t('device_limit_removing') : t('device_limit_remove')}
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-500 dark:text-neutral-400">{t('device_limit_team_hint')}</p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <a href="/pricing" className="text-sm underline underline-offset-4 hover:no-underline">
            {t('device_limit_team_cta')}
          </a>
          <button
            // Through the hook: it clears the local session and the app state
            // even when the server logout fails, which drops `user` and this modal.
            onClick={() => { void signOut(); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-900 dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-200 text-white dark:text-neutral-900 text-xs font-mono font-semibold uppercase tracking-wide transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {t('device_limit_signout')}
            <span aria-hidden="true">&rarr;</span>
          </button>
        </div>
      </div>
    </div>
  );
}
